import { useState } from "react";
import { defaultKpiTargets, type KpiTargets, type KpiTargetValues } from "@/mocks/rop";

function formatMoney(value: number): string {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(value);
}

type GradeKey = "Менеджер" | "Ведущий менеджер";

const gradeTabs: { key: GradeKey; label: string; icon: string }[] = [
  { key: "Менеджер", label: "Менеджер", icon: "ri-user-line" },
  { key: "Ведущий менеджер", label: "Ведущий менеджер", icon: "ri-vip-crown-line" },
];

const fieldDefs: { key: keyof KpiTargetValues; label: string; icon: string; unit: string; isMoney: boolean }[] = [
  { key: "calls", label: "Звонки", icon: "ri-phone-line", unit: "шт.", isMoney: false },
  { key: "proposals", label: "КП (коммерческие предложения)", icon: "ri-mail-send-line", unit: "шт.", isMoney: false },
  { key: "newClients", label: "Новые клиенты", icon: "ri-user-add-line", unit: "чел.", isMoney: false },
  { key: "meetings", label: "Встречи", icon: "ri-calendar-check-line", unit: "шт.", isMoney: false },
  { key: "invoices", label: "Счета", icon: "ri-file-list-3-line", unit: "шт.", isMoney: false },
  { key: "payments", label: "Оплаты", icon: "ri-bank-card-line", unit: "шт.", isMoney: false },
  { key: "revenue", label: "Выручка (план)", icon: "ri-money-dollar-circle-line", unit: "₽", isMoney: true },
  { key: "margin", label: "Маржа (план)", icon: "ri-funds-line", unit: "₽", isMoney: true },
];

export default function KpiSettingsTab() {
  const [activeGrade, setActiveGrade] = useState<GradeKey>("Менеджер");
  const [targets, setTargets] = useState<KpiTargets>(() => JSON.parse(JSON.stringify(defaultKpiTargets)));
  const [saved, setSaved] = useState(false);

  const currentTargets = targets[activeGrade];

  const handleChange = (field: keyof KpiTargetValues, rawValue: string) => {
    const num = rawValue === "" ? 0 : Number(rawValue.replace(/\D/g, ""));
    setTargets((prev) => ({
      ...prev,
      [activeGrade]: { ...prev[activeGrade], [field]: num },
    }));
    setSaved(false);
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleReset = () => {
    setTargets(JSON.parse(JSON.stringify(defaultKpiTargets)));
    setSaved(false);
  };

  const formatInputValue = (field: keyof KpiTargetValues): string => {
    const val = currentTargets[field];
    return String(val);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[13px] font-semibold text-[#2D2D2D]">Настройка KPI-планов по грейдам</h2>
          <p className="text-[10px] text-[#666666]">Настройте целевые показатели для каждого грейда менеджеров</p>
        </div>
      </div>

      {/* Grade Tabs */}
      <div className="flex items-center gap-0 border-b border-[#D5CA8A]">
        {gradeTabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => { setActiveGrade(tab.key); setSaved(false); }}
            className={`flex items-center gap-1.5 px-4 py-2 text-[12px] font-medium cursor-pointer transition-colors border-b-2 -mb-[1px] whitespace-nowrap ${
              activeGrade === tab.key
                ? "text-[#2D2D2D] border-[#D97706] bg-white rounded-t"
                : "text-[#666666] border-transparent hover:text-[#2D2D2D] hover:bg-[#FDF2B5]/50 rounded-t"
            }`}
          >
            <i className={`${tab.icon} text-[13px]`}></i>
            {tab.label}
          </button>
        ))}
        <span className="ml-auto text-[10px] text-[#666666]">
          Менеджеров этого грейда: {
            activeGrade === "Менеджер" ? "9" : "3"
          }
        </span>
      </div>

      {/* Fields */}
      <div className="bg-white rounded-lg border border-[#D5CA8A] p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {fieldDefs.map((field) => (
            <div key={field.key} className="flex flex-col gap-1.5">
              <label className="flex items-center gap-1.5 text-[11px] font-medium text-[#2D2D2D]">
                <i className={`${field.icon} text-[12px] text-[#666666]`}></i>
                {field.label}
              </label>
              <div className="relative">
                {field.isMoney ? (
                  <input
                    type="text"
                    value={formatInputValue(field.key)}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    className="w-full px-3 py-2 text-[13px] text-[#2D2D2D] bg-white border border-[#D5CA8A] rounded-md focus:outline-none focus:border-[#D97706] focus:ring-1 focus:ring-[#D97706]/30 transition-colors pr-14"
                  />
                ) : (
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatInputValue(field.key)}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                      handleChange(field.key, v);
                    }}
                    className="w-full px-3 py-2 text-[13px] text-[#2D2D2D] bg-white border border-[#D5CA8A] rounded-md focus:outline-none focus:border-[#D97706] focus:ring-1 focus:ring-[#D97706]/30 transition-colors pr-14"
                  />
                )}
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-[#999999] pointer-events-none">
                  {field.unit}
                </span>
              </div>
              {field.isMoney && (
                <span className="text-[9px] text-[#666666]">{formatMoney(currentTargets[field.key])}</span>
              )}
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="mt-4 p-3 bg-[#FDF2B5]/40 rounded-lg border border-[#D5CA8A]/60">
          <p className="text-[10px] font-semibold text-[#2D2D2D] mb-1">Текущий план для «{activeGrade}»:</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-[#666666]">
            <span>{currentTargets.calls} звонков</span>
            <span>{currentTargets.proposals} КП</span>
            <span>{currentTargets.newClients} новых клиентов</span>
            <span>{currentTargets.meetings} встреч</span>
            <span>{currentTargets.invoices} счетов</span>
            <span>{currentTargets.payments} оплат</span>
            <span>Выручка: {formatMoney(currentTargets.revenue)}</span>
            <span>Маржа: {formatMoney(currentTargets.margin)}</span>
          </div>
        </div>

        {saved && (
          <div className="mt-3 p-2 bg-[#D4EDDA] rounded-md border border-[#2D6A4F]/30 text-[11px] text-[#2D6A4F] flex items-center gap-1.5">
            <i className="ri-check-line text-[12px]"></i>
            Планы сохранены успешно!
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#D5CA8A]/60">
          <button
            onClick={handleReset}
            className="flex items-center gap-1 px-3 py-1.5 rounded text-[11px] text-[#DC2626] hover:bg-[#FEE2E2] cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-refresh-line text-[12px]"></i>
            Сбросить к значениям по умолчанию
          </button>
          <button
            onClick={handleSave}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded text-[11px] font-medium bg-[#E8DB6C] hover:bg-[#D4C55A] text-[#2D2D2D] cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-save-line text-[12px]"></i>
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
}